/**
 * The career screens, as data.
 *
 * Pure: state in, strings out, so the pilot sheet and the consequences screen
 * can be asserted without mounting a renderer — the same discipline
 * `mission/briefing-card.ts` follows for the mission select screen.
 *
 * Field labels are the original's own where the original has one: VIEW PILOT
 * RECORD's `SCORE` / `SQUADRON NAME` / `AERODROME LOCATION` / `PLANE TYPE` /
 * `DATE` / `AIRCRAFT` / `BALLOONS` / `ZEPPELINS` / `TOTAL VICTORIES` /
 * `MEDALS`, and the Aerodrome menu's own entry names.
 */

import {
  AWARD_HEADER,
  BOARD_HEADER,
  CAREER_RANKS,
  ENLISTMENT_LABELS,
  SERVICE_LABELS,
  citationForMedal,
  currentAircraft,
  medalPictureFrame,
  formatCareerDate,
  personalAircraftLabel,
  pilotRecord,
  rankOf,
  serviceFor,
  squadronOf,
  aircraftLabel,
  type CareerConsequence,
  type CareerState,
  type EnlistmentPeriod,
  type Nation,
} from "@red-opus/engine";

export interface SheetRow {
  readonly label: string;
  readonly value: string;
}

function row(label: string, value: string): SheetRow {
  return { label, value };
}

/** VIEW PILOT RECORD, in the original's field order. */
export function pilotSheet(career: CareerState): readonly SheetRow[] {
  const record = pilotRecord(career);
  return [
    row("STATUS", record.status),
    row("SCORE", String(record.score)),
    row("SQUADRON NAME", record.squadron),
    row("AERODROME LOCATION", `${record.aerodrome}, ${record.region} region`),
    row("PLANE TYPE", personalAircraftLabelOf(career) ?? record.planeType),
    row("DATE", record.date),
    row("AIRCRAFT", String(record.aircraft)),
    row("BALLOONS", String(record.balloons)),
    row("ZEPPELINS", String(record.zeppelins)),
    row("TOTAL VICTORIES", String(record.totalVictories)),
    row("UNCONFIRMED CLAIMS", String(record.unconfirmed)),
    row("MISSIONS FLOWN", String(record.missionsFlown)),
    row("WOUNDS", String(record.wounds)),
    row("BOARDS OF INQUIRY", String(record.boardsOfInquiry)),
  ];
}

function personalAircraftLabelOf(career: CareerState): string | undefined {
  const label = personalAircraftLabel(career);
  if (!label) return undefined;
  return label.replace(currentAircraft(career), aircraftLabel(currentAircraft(career)));
}

/** The line under the pilot's name: rank, service, squadron. */
export function pilotHeadline(career: CareerState): string {
  const service = SERVICE_LABELS[serviceFor(career.nation, career.date)];
  return `${rankOf(career.nation, career.rank).title} · ${squadronOf(career).name} · ${service}`;
}

/** What the aerodrome can offer him today, in a word. */
export function dutyLine(career: CareerState): string {
  if (career.status === "hospital") {
    return `In hospital. Back on duty ${career.dutyReturnDate ? formatCareerDate(career.dutyReturnDate) : "shortly"}.`;
  }
  if (career.status === "pow") {
    return `A prisoner of war. Expected back over the wire ${career.dutyReturnDate ? formatCareerDate(career.dutyReturnDate) : "in time"}.`;
  }
  if (career.status === "dead") return "Killed in action.";
  if (career.status === "discharged") return "Grounded permanently by the Board of Inquiry.";
  if (career.status === "retired") return "The war is over.";
  return `On duty at ${squadronOf(career).aerodrome}.`;
}

export interface EnlistOption<T> {
  readonly value: T;
  readonly label: string;
  readonly detail: string;
}

/** The two playable services, and what each one calls its ranks. */
export function serviceOptions(): readonly EnlistOption<Nation>[] {
  return [
    {
      value: "allied",
      label: SERVICE_LABELS.rfc,
      detail: `${CAREER_RANKS.allied.map((rank) => rank.title).join(" → ")} · becomes the ${SERVICE_LABELS.raf} on 1 April 1918`,
    },
    {
      value: "german",
      label: SERVICE_LABELS.gas,
      detail: CAREER_RANKS.german.map((rank) => rank.title).join(" → "),
    },
  ];
}

/** The three enlistment periods, with the date each one starts on. */
export function enlistmentOptions(): readonly EnlistOption<EnlistmentPeriod>[] {
  return [
    { value: "early", label: ENLISTMENT_LABELS.early, detail: "December of 1915 — Eindeckers rule the front" },
    { value: "middle", label: ENLISTMENT_LABELS.middle, detail: "October of 1916 — the Albatros arrives" },
    { value: "late", label: ENLISTMENT_LABELS.late, detail: "January of 1918 — ten months to the armistice" },
  ];
}

/**
 * A consequence the original gives a **screen** rather than a popup: the medal
 * citation, the promotion card, the Board of Inquiry. `shell/model/
 * ceremony-screens.ts` draws these; everything else stays in the popup the
 * between-missions dialog has always been.
 */
export type CeremonyKind = "citation" | "promotion" | "board";

export interface CeremonySpec {
  readonly kind: CeremonyKind;
  /** `MEDALS.BMP` frame for a citation, when the port has a picture for that medal. */
  readonly picture: number | undefined;
  /** The rank just conferred, for the promotion card's port-side title. */
  readonly rank: string;
}

export interface ConsequenceSection {
  readonly heading: string;
  readonly lines: readonly string[];
  /** Marks a section the screen should show in red: a wound, a board, a death. */
  readonly grave: boolean;
  /** Set when this step is a ceremony screen instead of a popup. */
  readonly ceremony: CeremonySpec | undefined;
}

function section(heading: string, lines: readonly string[], grave = false, ceremony?: CeremonySpec): ConsequenceSection {
  return { heading, lines, grave, ceremony };
}

/** Which `MEDALS.BMP` frame a decoration shows, or nothing for one the file has no citation for. */
function medalFrame(medalId: string): number | undefined {
  const citation = citationForMedal(medalId);
  return citation ? medalPictureFrame(citation) : undefined;
}

/** The between-missions screen: claims, decorations, promotion, discipline, wounds. */
export function consequenceSections(consequence: CareerConsequence): readonly ConsequenceSection[] {
  const sections: ConsequenceSection[] = [];
  const claims = consequence.claims.map(
    (claim) => `${claim.confirmed ? "CONFIRMED" : "UNCONFIRMED"} — ${claim.record.label}. ${claim.reason}`,
  );
  sections.push(
    section("VICTORY CLAIMS", claims.length > 0 ? claims : ["No claims submitted."]),
  );
  for (const [index, citation] of consequence.citations.entries()) {
    const award = consequence.awards[index];
    sections.push(
      section(AWARD_HEADER, citation, false, {
        kind: "citation",
        picture: award ? medalFrame(award.medalId) : undefined,
        rank: "",
      }),
    );
  }
  if (consequence.promotion) {
    sections.push(
      section("PROMOTION", [consequence.promotion.message], false, {
        kind: "promotion",
        picture: undefined,
        rank: consequence.promotion.title,
      }),
    );
  }
  if (consequence.hearing) {
    sections.push(
      section(BOARD_HEADER, [consequence.hearing.accusation, ...consequence.hearing.lines], true, {
        kind: "board",
        picture: undefined,
        rank: "",
      }),
    );
  }
  if (consequence.casualty) sections.push(section("CASUALTY REPORT", [consequence.casualty.text], true));
  for (const introduction of consequence.introductions) {
    sections.push(section("AIRCRAFT INTRODUCTION", [introduction.message]));
  }
  if (consequence.ending) {
    sections.push(
      section(consequence.ending.outcome.header ?? "END OF CAREER", [
        consequence.ending.outcome.narrative,
        consequence.ending.epitaph,
      ], consequence.ending.status !== "retired"),
    );
  }
  return sections;
}

/** `"1 October 1916 · Duel: Edward Mannock · +353"` for the consequences header. */
export function consequenceHeadline(consequence: CareerConsequence): string {
  const score = consequence.missionScore >= 0 ? `+${consequence.missionScore}` : String(consequence.missionScore);
  return `${formatCareerDate(consequence.date)} · ${consequence.missionTitle} · ${score}`;
}
